import React from 'react';
import { Box, Stack, Typography, alpha, useTheme } from '@mui/material';
import {
  Image as ImageIcon, Video, File, Phone, ExternalLink, CornerDownLeft,
} from 'lucide-react';
import { normalizeButtons } from './waData';
import { STATIC_IMAGE_URL, API_URL } from '../../utils/axios';

const BACKEND_URL = (API_URL || '').replace(/\/api\/?$/, '');

// A stored media path is server-relative (e.g. /wa_template_media/x.jpg); make it
// absolute for <img> src. Blob/data/http URLs (a freshly picked file) pass through.
const mediaSrc = (url) => {
  if (!url) return '';
  if (/^(blob:|data:|https?:)/.test(url)) return url;
  const path = url.startsWith('/') ? url : `/${url}`;
  if (path.startsWith('/wa_template_media')) {
    return `${BACKEND_URL}${path}`;
  }
  return `${STATIC_IMAGE_URL}${path}`;
};

// Parse WhatsApp-style formatting into React nodes:  *bold*  _italic_  ~strike~  ```mono```
const RICH_RE = /(```[\s\S]+?```|\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~)/g;
const renderRichText = (text) =>
  String(text).split(RICH_RE).filter((p) => p !== undefined && p !== '').map((p, i) => {
    if (p.startsWith('```') && p.endsWith('```')) {
      return <Box key={i} component="span" sx={{ fontFamily: 'monospace', bgcolor: 'rgba(0,0,0,0.06)', px: 0.5, borderRadius: 0.5 }}>{p.slice(3, -3)}</Box>;
    }
    if (p.startsWith('*') && p.endsWith('*')) return <strong key={i}>{p.slice(1, -1)}</strong>;
    if (p.startsWith('_') && p.endsWith('_')) return <em key={i}>{p.slice(1, -1)}</em>;
    if (p.startsWith('~') && p.endsWith('~')) return <span key={i} style={{ textDecoration: 'line-through' }}>{p.slice(1, -1)}</span>;
    return p;
  });

// Substitute {{var}} placeholders with their saved example values for the preview.
const VAR_RE = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;
const substituteVars = (text = '', samples = {}) =>
  String(text).replace(VAR_RE, (m, n) => (samples[n] && String(samples[n]).trim() ? samples[n] : m));
const varsToSamples = (variables = []) =>
  variables.reduce((acc, v) => { if (v && v.name) acc[v.name] = v.example || ''; return acc; }, {});

const ButtonIcon = ({ type, size = 14 }) => {
  if (type === 'PHONE_NUMBER') return <Phone size={size} />;
  if (type === 'URL') return <ExternalLink size={size} />;
  return <CornerDownLeft size={size} />;
};

// Map a stored template (from the API) to the fields the bubble needs.
const toPreviewForm = (t = {}) => ({
  headerType: t.headerType || 'NONE',
  header: t.header || '',
  headerMediaName: t.headerMediaName || '',
  headerMediaUrl: mediaSrc(t.headerMediaUrl),
  body: t.body || '',
  footer: t.footer || '',
  buttons: normalizeButtons(t.buttons || []),
  samples: varsToSamples(t.variables || []),
});

const WaTemplatePreview = ({ template, compact = false, showLabel = true }) => {
  const theme = useTheme();
  const form = toPreviewForm(template);
  const bodyFontSize = compact ? 12.5 : 13.5;
  const samples = form.samples || {};
  const buttons = (form.buttons || []).filter((b) => b.text && b.text.trim());
  // WhatsApp attaches call-to-action buttons (links/phone) to the bottom of the
  // bubble, but renders quick-reply buttons as separate pills below it.
  const ctaButtons = buttons.filter((b) => b.type === 'URL' || b.type === 'PHONE_NUMBER');
  const quickButtons = buttons.filter((b) => b.type === 'QUICK_REPLY');
  return (
    <Box sx={{ p: compact ? 1.25 : 1.5, borderRadius: 2,
      bgcolor: theme.palette.mode === 'dark' ? alpha('#0b141a', 0.5) : '#efeae2',
      display: 'flex', flexDirection: 'column' }}>
      {showLabel && <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: 'text.secondary', mb: 0.75 }}>PREVIEW</Typography>}
      <Box sx={{ alignSelf: 'flex-start', maxWidth: '95%', bgcolor: theme.palette.background.paper,
        borderRadius: 2, borderTopLeftRadius: 2, p: 1.25, boxShadow: theme.shadows[1] }}>
        {(form.headerType === 'IMAGE' || form.headerType === 'VIDEO' || form.headerType === 'DOCUMENT') && (
          form.headerMediaUrl ? (
            form.headerType === 'IMAGE' ? (
              <Box
                component="img"
                src={form.headerMediaUrl}
                alt="header"
                sx={{
                  width: '100%',
                  maxHeight: 120,
                  objectFit: 'cover',
                  borderRadius: 1,
                  mb: 0.75,
                  display: 'block',
                  bgcolor: (theme) => alpha(theme.palette.divider, 0.1),
                }}
                onError={(e) => {
                  const currentSrc = e.currentTarget.src || '';
                  if (!e.currentTarget.dataset.retried) {
                    e.currentTarget.dataset.retried = 'true';
                    if (currentSrc.includes('amazonaws.com') && BACKEND_URL) {
                      const filename = currentSrc.split('/').pop();
                      e.currentTarget.src = `${BACKEND_URL}/wa_template_media/${filename}`;
                    } else if (STATIC_IMAGE_URL && !currentSrc.includes('amazonaws.com')) {
                      const filename = currentSrc.split('/').pop();
                      e.currentTarget.src = `${STATIC_IMAGE_URL}/wa_template_media/${filename}`;
                    }
                  }
                }}
              />
            ) : form.headerType === 'VIDEO' ? (
              <Box component="video" src={form.headerMediaUrl} controls
                sx={{ width: '100%', maxHeight: 120, objectFit: 'contain', borderRadius: 1, mb: 0.75, display: 'block', bgcolor: '#000' }} />
            ) : (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 1,
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                mb: 0.75
              }}>
                <Box sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 0.5,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <File size={16} color={theme.palette.primary.main} />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.primary', fontFamily: 'Inter, sans-serif' }} noWrap>
                    {form.headerMediaName || 'Document.pdf'}
                  </Typography>
                  <Typography sx={{ fontSize: 10, color: 'text.secondary', fontFamily: 'Inter, sans-serif' }}>
                    Document Template
                  </Typography>
                </Box>
              </Box>
            )
          ) : (
            <Box sx={{ height: form.headerType === 'DOCUMENT' ? 44 : 72, borderRadius: 1, mb: 0.75, display: 'flex',
              alignItems: 'center', justifyContent: 'center', gap: 0.5,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.18)}, ${alpha(theme.palette.primary.main, 0.08)})` }}>
              {form.headerType === 'VIDEO' ? <Video size={20} color={theme.palette.primary.main} />
                : form.headerType === 'DOCUMENT' ? <File size={18} color={theme.palette.primary.main} />
                : <ImageIcon size={20} color={theme.palette.primary.main} />}
              <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: 'text.secondary' }} noWrap>
                {form.headerType === 'VIDEO' ? '[Video]' : form.headerType === 'DOCUMENT' ? '[Document]' : '[Image]'}
              </Typography>
            </Box>
          )
        )}
        {form.headerType === 'TEXT' && form.header && <Typography sx={{ fontSize: bodyFontSize, fontWeight: 800, mb: 0.5 }}>{renderRichText(substituteVars(form.header, samples))}</Typography>}
        <Typography sx={{ fontSize: bodyFontSize, lineHeight: 1.45, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {form.body ? renderRichText(substituteVars(form.body, samples)) : 'No preview text for this template.'}
        </Typography>
        {form.footer && <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 0.75 }}>{form.footer}</Typography>}
        {ctaButtons.length > 0 && (
          <Box sx={{ mt: 1, mx: -1.25 }}>
            {ctaButtons.map((b, i) => (
              <Stack key={i} direction="row" spacing={0.75} alignItems="center" justifyContent="center"
                sx={{ borderTop: `1px solid ${theme.palette.divider}`, py: 0.75, px: 1, color: '#00a5f4', fontSize: 13, fontWeight: 600 }}>
                <ButtonIcon type={b.type} />
                <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.text}</Box>
              </Stack>
            ))}
          </Box>
        )}
      </Box>
      {quickButtons.length > 0 && (
        <Stack spacing={0.5} sx={{ mt: 0.75, alignSelf: 'flex-start', width: '95%' }}>
          {quickButtons.map((b, i) => (
            <Stack key={i} direction="row" spacing={0.5} alignItems="center" justifyContent="center"
              sx={{ bgcolor: theme.palette.background.paper, borderRadius: 2, py: 0.75, px: 1, fontSize: 13, fontWeight: 700, color: '#00a5f4', boxShadow: theme.shadows[1] }}>
              <ButtonIcon type={b.type} />
              <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.text}</Box>
            </Stack>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default WaTemplatePreview;
